// src/common/components/button/Button.tsx
import React from "react";
import { IconType } from "react-icons";
import { useTheme } from "../../hooks/useTheme";
import Loader from "../loader/Loader";
import styles from "./Button.module.scss";

export type ButtonVariant = "primary" | "secondary" | "outline";
export type ButtonSize = "small" | "medium" | "large";

interface ButtonProps {
  title: string;
  onClick: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  icon?: React.ReactElement<IconType>;
  iconPosition?: "left" | "right";
  uppercase?: boolean;
  badge?: string;
  fullWidth?: boolean;
  textStyle?: React.CSSProperties;
}

export default function Button({
  title,
  onClick,
  variant = "primary",
  size = "medium",
  className = "",
  style,
  disabled = false,
  loading = false,
  loadingText,
  icon,
  iconPosition = "left",
  uppercase = false,
  badge,
  fullWidth = false,
  textStyle,
}: ButtonProps) {
  const { theme } = useTheme();

  const getVariantStyle = () => {
    switch (variant) {
      case "primary":
        return styles.primary;
      case "secondary":
        return styles.secondary;
      case "outline":
        return styles.outline;
      default:
        return styles.primary;
    }
  };

  const getSizeStyle = () => {
    switch (size) {
      case "small":
        return styles.small;
      case "medium":
        return styles.medium;
      case "large":
        return styles.large;
      default:
        return styles.medium;
    }
  };

  const getIconColor = () => {
    switch (variant) {
      case "primary":
        return "#fff";
      case "secondary":
        return theme.textSecondary;
      case "outline":
        return theme.primary;
      default:
        return "#fff";
    }
  };

  const badgeStyles = {
    backgroundColor: variant === "primary" ? "#fff" : theme.primary,
    borderColor: variant === "primary" ? theme.primary : "#fff",
    color: variant === "primary" ? theme.primary : "#fff",
  };

  const isDisabled = disabled || loading;

  return (
    <button
      className={`
        ${styles.button} 
        ${getVariantStyle()} 
        ${getSizeStyle()} 
        ${isDisabled ? styles.disabled : ""} 
        ${fullWidth ? styles.fullWidth : ""}
        ${className}
      `}
      style={style}
      onClick={onClick}
      disabled={isDisabled}
    >
      <div className={styles.container}>
        <div
          className={`${styles.content} ${iconPosition === "right" ? styles.contentReverse : ""}`}
        >
          {!loading && icon && iconPosition === "left" && (
            <span className={styles.icon}>{icon}</span>
          )}
          {loading ? (
            <Loader size="small" color={getIconColor()} />
          ) : (
            <span
              className={`${styles.text} ${uppercase ? styles.uppercase : ""}`}
              style={textStyle}
            >
              {title}
            </span>
          )}
          {!loading && icon && iconPosition === "right" && (
            <span className={styles.icon}>{icon}</span>
          )}
        </div>

        {badge && (
          <div
            className={styles.badge}
            style={{
              backgroundColor: badgeStyles.backgroundColor,
              borderColor: badgeStyles.borderColor,
            }}
          >
            <span
              className={styles.badgeText}
              style={{ color: badgeStyles.color }}
            >
              {badge}
            </span>
          </div>
        )}
      </div>
    </button>
  );
}